import { useState } from "react";
import { C, PageHeader, GlassCard, Badge, Skeleton, ProgressBar } from "@coremonit/design-system";
import { Flame, Database, Cpu, Zap, MemoryStick } from "lucide-react";
import { useAsyncWait, useFlamegraph, useHeapAnalysis, useHotMethods, useServices, useSlowQueries } from "../api/hooks";
import type { FlameNode } from "@coremonit/types";

export function ProfilingPage() {
  const services = useServices();
  const [serviceId, setServiceId] = useState<string | undefined>();
  const flame = useFlamegraph(serviceId);
  const hot = useHotMethods(serviceId);
  const slow = useSlowQueries();
  const heap = useHeapAnalysis();
  const asyncWait = useAsyncWait();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader icon={<Flame size={16} />} title="Performance Profiling" subtitle="Flamegraph · hot methods · slow SQL · heap analysis · async waits" />
      <GlassCard style={{ padding: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ color: C.textMuted, fontSize: 11 }}>Service</span>
          <select value={serviceId ?? ""} onChange={(e) => setServiceId(e.target.value || undefined)}
            style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: "6px 10px", fontSize: 12, minWidth: 260 }}>
            <option value="">Select a service…</option>
            {services.data?.items.map((s) => <option key={s.id} value={s.id} style={{ background: C.surface }}>{s.name}</option>)}
          </select>
        </div>
      </GlassCard>

      <GlassCard style={{ padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, display: "flex", alignItems: "center", gap: 6 }}><Flame size={12} color={C.red} />CPU Flamegraph</div>
          <Badge color="blue">1000 samples</Badge>
        </div>
        {!serviceId ? (
          <div style={{ padding: 20, color: C.textMuted, textAlign: "center", fontSize: 12 }}>Select a service.</div>
        ) : flame.isLoading ? <Skeleton height={220} /> : flame.data && (
          <Flamegraph node={flame.data.flamegraph} maxWidth={900} />
        )}
      </GlassCard>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <GlassCard style={{ padding: 14 }}>
          <SectionTitle icon={<Cpu size={12} />} title="Hot methods" />
          {!serviceId ? <Empty msg="Select a service." /> : hot.isLoading ? <Skeleton height={200} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {hot.data?.items.map((h, i) => (
                <div key={i} style={{ padding: 10, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <div style={{ color: C.text, fontSize: 11, fontFamily: "ui-monospace, monospace" }}>{h.method}</div>
                    <span style={{ color: C.blue, fontSize: 12, fontWeight: 700 }}>{h.pct}%</span>
                  </div>
                  <ProgressBar value={h.pct} color={C.blue} />
                  <div style={{ color: C.textMuted, fontSize: 9, marginTop: 3 }}>{h.samples.toLocaleString()} samples · {h.language}</div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
        <GlassCard style={{ padding: 14 }}>
          <SectionTitle icon={<Database size={12} />} title="Slow SQL" />
          {slow.isLoading ? <Skeleton height={200} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {slow.data?.items.map((s, i) => (
                <div key={i} style={{ padding: 10, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ color: C.blue, fontSize: 10, fontWeight: 700 }}>{s.service}</span>
                    <span style={{ color: s.p95Ms > 1000 ? C.red : C.amber, fontSize: 12, fontWeight: 700 }}>p95 {s.p95Ms}ms</span>
                  </div>
                  <div style={{ color: C.text, fontSize: 10, fontFamily: "ui-monospace, monospace", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 6 }}>{s.query}</div>
                  <div style={{ color: C.textMuted, fontSize: 9, marginTop: 4 }}>{s.count.toLocaleString()} calls · avg {s.avgMs}ms</div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
        <GlassCard style={{ padding: 14 }}>
          <SectionTitle icon={<MemoryStick size={12} />} title="Heap analysis" />
          {heap.isLoading ? <Skeleton height={180} /> : heap.data && (
            <>
              <div style={{ color: C.text, fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{heap.data.totalMb} MB total</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {heap.data.breakdown.map((b, i) => (
                  <div key={i}>
                    <div style={{ display: "flex", justifyContent: "space-between", color: C.textSub, fontSize: 11, marginBottom: 3 }}>
                      <span>{b.label}</span><span style={{ color: C.text, fontWeight: 700 }}>{b.mb} MB</span>
                    </div>
                    <ProgressBar value={(b.mb / heap.data.totalMb) * 100} color={C.purple} height={5} />
                  </div>
                ))}
              </div>
            </>
          )}
        </GlassCard>
        <GlassCard style={{ padding: 14 }}>
          <SectionTitle icon={<Zap size={12} />} title="Async / blocking waits" />
          {asyncWait.isLoading ? <Skeleton height={180} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {asyncWait.data?.items.map((a, i) => (
                <div key={i} style={{ padding: 10, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{a.name}</div>
                    <div style={{ color: C.textMuted, fontSize: 9 }}>{a.samples.toLocaleString()} samples</div>
                  </div>
                  <span style={{ color: C.amber, fontSize: 12, fontWeight: 700 }}>{a.waitMs}ms</span>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

function SectionTitle({ icon, title }: { icon: React.ReactNode; title: string }) {
  return <div style={{ display: "flex", alignItems: "center", gap: 6, color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>{icon}{title}</div>;
}
function Empty({ msg }: { msg: string }) { return <div style={{ padding: 20, color: C.textMuted, textAlign: "center", fontSize: 12 }}>{msg}</div>; }

function Flamegraph({ node, maxWidth }: { node: FlameNode; maxWidth: number }) {
  const layers: Array<Array<{ n: FlameNode; x: number; w: number }>> = [];
  const walk = (n: FlameNode, depth: number, x: number, w: number) => {
    (layers[depth] ??= []).push({ n, x, w });
    if (!n.children) return;
    const total = n.children.reduce((a, c) => a + c.value, 0);
    let cursor = x;
    for (const c of n.children) {
      const cw = (c.value / total) * w;
      walk(c, depth + 1, cursor, cw);
      cursor += cw;
    }
  };
  walk(node, 0, 0, maxWidth);
  const rowH = 22;
  return (
    <div style={{ position: "relative", overflow: "auto" }}>
      <svg width={maxWidth} height={layers.length * rowH + 8}>
        {layers.map((row, d) =>
          row.map((cell, i) => {
            const color = d === 0 ? C.red : d === 1 ? C.amber : d === 2 ? C.blue : C.purple;
            return (
              <g key={`${d}-${i}`} transform={`translate(${cell.x}, ${d * rowH})`}>
                <rect width={Math.max(1, cell.w - 1)} height={rowH - 2} rx={3} fill={color} opacity={0.6} />
                {cell.w > 60 && (
                  <text x={4} y={rowH - 8} fill="#fff" fontSize={9.5} style={{ pointerEvents: "none", fontFamily: "ui-monospace, monospace" }}>
                    {cell.n.name.length > cell.w / 6 ? cell.n.name.slice(0, Math.floor(cell.w / 6)) + "…" : cell.n.name}
                  </text>
                )}
              </g>
            );
          })
        )}
      </svg>
    </div>
  );
}
